// ─────────────────────────────────────────────────────────────────────────────
// StartupSweepGate.jsx
// Route coordinator for the Startup Readiness Sweep (spec §3.1, §4.4).
//
// Responsibilities:
//  1. Run resolveMode() to decide sweep mode
//  2. Fetch DashboardSnapshot (non-blocking)
//  3. Run the timing controller
//  4. Render StartupSweepOverlay on top of children while active
//  5. Remove overlay once SETTLED
//
// V1 scope: Admin Dashboard full sweep only.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback, useMemo } from 'react';
import { useDashboardSnapshot } from './DashboardSnapshotProvider';
import {
  useStartupSweepController,
  detectColdLaunch,
  isFeatureEnabled,
  prefersReducedMotion,
} from './StartupSweepController';
import { fromSnapshot } from './LandingPriorityResolver';
import StartupSweepOverlay from './StartupSweepOverlay';

// ── Mode resolution (spec §4.4) ────────────────────────────────────────────
// Priority order:
//  1. !coldLaunch         → DISABLED
//  2. !featureFlag        → DISABLED
//  3. platformDisable     → REDUCED_MOTION
//  4. deep-link / resume  → DISABLED  (path !== '/dashboard/app')
//  5. kiosk route         → KIOSK_MICRO  (not in V1)
//  6. iPad admin          → ADMIN_COMPACT (not in V1)
//  7. admin dashboard     → ADMIN_FULL
//  8. else                → DISABLED
function resolveMode({ isColdLaunch, routeKey }) {
  if (!isColdLaunch)           return 'DISABLED';
  if (!isFeatureEnabled())     return 'DISABLED';
  if (prefersReducedMotion())  return 'REDUCED_MOTION';
  // Deep-link guard: only trigger on the primary dashboard route
  if (routeKey !== 'dashboard') return 'DISABLED';
  return 'ADMIN_FULL';
}

// ── Gate component ─────────────────────────────────────────────────────────
/**
 * @param {object} props
 * @param {string} props.routeKey  'dashboard' | 'kiosk' | other
 * @param {React.ReactNode} props.children
 */
export default function StartupSweepGate({ routeKey = 'dashboard', children }) {
  // Resolve mode once on mount — cold launch detection consumes the flag
  const mode = useMemo(
    () => resolveMode({ isColdLaunch: detectColdLaunch(), routeKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Overlay mounted state — remove from DOM once settled to free resources
  const [overlayMounted, setOverlayMounted] = useState(mode !== 'DISABLED');

  const handleSettled = useCallback(() => {
    setOverlayMounted(false);
  }, []);

  const { phase, tracerPct, fastForward } = useStartupSweepController({
    mode: overlayMounted ? mode : 'DISABLED',
    onSettled: handleSettled,
  });

  const { snapshot } = useDashboardSnapshot();
  const priority = useMemo(() => fromSnapshot(snapshot), [snapshot]);

  return (
    <>
      {children}
      {overlayMounted && (
        <StartupSweepOverlay
          phase={phase}
          tracerPct={tracerPct}
          snapshot={snapshot}
          priority={priority}
          onFastForward={fastForward}
        />
      )}
    </>
  );
}
